if (!customElements.get('yt-exploded-hero')) {
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const smooth = (a, b, v) => {
    const t = clamp01((v - a) / (b - a));
    return t * t * (3 - 2 * t);
  };
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  // Carousel (assembled hero), by a map's distance `a` from the middle: within SETTLE its sketch
  // lies exactly on the relief in the frame and the rendered map fades into it; further out the
  // sketch slides towards its side slot at 1. The frame itself never moves.
  const SETTLE = 0.3;
  const mapAlpha = (a) => 1 - smooth(0.04, SETTLE, a);
  const sketchAlpha = (a) => smooth(0, 0.2, a);
  // 0 within SETTLE, 1 at the side slot, leaving it with slope `m` (per unit of t) so the
  // motion carries on into the outer slots without a jolt.
  const travel = (a, m = 1) => {
    const t = clamp01((a - SETTLE) / (1 - SETTLE));
    return (3 - m) * t * t + (m - 2) * t * t * t;
  };

  // fetch() sends Accept: */*, for which the Shopify CDN converts images to PNG (ten times the
  // bytes), so atlas requests name the formats this browser decodes. A browser without AVIF gets
  // the AVIF atlases converted to PNG by the CDN.
  const imageAccept = new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve(probe.width ? 'image/avif,image/webp,*/*' : 'image/webp,*/*');
    probe.onerror = () => resolve('image/webp,*/*');
    probe.src =
      'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAAGGbWV0YQAAAAAAAAAhaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAAAAAAAOcGl0bQAAAAAAAQAAACxpbG9jAAAAAEQAAAIAAQAAAAEAAAHCAAAAIQACAAAAAQAAAa4AAAAUAAAAQmlpbmYAAAAAAAIAAAAaaW5mZQIAAAAAAQAAYXYwMUNvbG9yAAAAABppbmZlAgAAAAACAABhdjAxQWxwaGEAAAAAGmlyZWYAAAAAAAAADmF1eGwAAgABAAEAAADDaXBycAAAAJ1pcGNvAAAAFGlzcGUAAAAAAAAAAQAAAAEAAAAQcGl4aQAAAAADCAgIAAAADGF2MUOBAAwAAAAAE2NvbHJuY2x4AAEADQAGgAAAAA5waXhpAAAAAAEIAAAADGF2MUOBABwAAAAAOGF1eEMAAAAAdXJuOm1wZWc6bXBlZ0I6Y2ljcDpzeXN0ZW1zOmF1eGlsaWFyeTphbHBoYQAAAAAeaXBtYQAAAAAAAAACAAEEAQKDBAACBAEFhgcAAAA9bWRhdBIACgQYAAYVMgoYACihAAIhHctgEgAKCBgABogIaDQgMhMZR4eGIYeeeeaAAACQQMkcYUK+';
  });

  // Coarse-to-fine load order (every 8th, 4th, 2nd frame, then the rest) so scrubbing works early.
  const loadOrder = (count) => {
    const seen = new Set();
    const order = [];
    for (const step of [8, 4, 2, 1]) {
      for (let i = 0; i < count; i += step) {
        if (!seen.has(i)) {
          seen.add(i);
          order.push(i);
        }
      }
    }
    if (!seen.has(count - 1)) order.push(count - 1);
    return order;
  };

  // Pre-rendered 3D sequence. Every frame is stacked from layer atlases back to front:
  // back, print, glass, relief, spacer + frame. Frame, glass and back are shared by all maps,
  // print + relief are per map, so switching maps only swaps (and animates) those two.
  // The frame itself is drawn on a second canvas on top, so the sketches of the neighbouring
  // maps (DOM canvases in between) pass behind it, at the depth of the map.
  class YtSequence {
    constructor(host, config) {
      this.host = host;
      this.base = config.meta;
      this.scene = host.querySelector('.yt-scene--seq');
      this.canvas = this.scene.querySelector('.yt-seq__canvas');
      this.ctx = this.canvas.getContext('2d');
      this.front = document.createElement('canvas');
      this.front.className = 'yt-seq__front';
      this.front.setAttribute('aria-hidden', 'true');
      this.canvas.after(this.front);
      this.fctx = this.front.getContext('2d');
      this.tags = [...this.scene.querySelectorAll('.yt-seq__tags [data-kind]')];
      this.nav = host.querySelector('.yt-seq__nav');
      this.mobile = window.matchMedia('(max-width: 860px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.meta = null;
      this.images = new Map();
      this.active = 0;
      this.map = 0;
      // Unwrapped, fractional map position of the carousel (rel() wraps it, so it loops forever).
      this.mpos = 0;
      this.index = 0;
      this.trans = null;
      this.started = false;
      this.interacted = false;
      this.drawQueued = false;
      this.variant = this.mobile.matches ? 'm' : 'd';
      this.mobile.addEventListener('change', () => this.reset());
      window.addEventListener('resize', () => {
        this.measureSketches();
        this.placeSketches();
      });

      Promise.all([fetch(this.base).then((response) => response.json()), imageAccept])
        .then(([meta, accept]) => {
          this.meta = meta;
          this.accept = accept;
          this.buildNav();
          this.bindDrag();
          this.reset();
          host.center?.();
        })
        .catch(() => {});
    }

    get maps() {
      return this.meta ? this.meta.maps : [];
    }

    wrap(i) {
      const n = this.maps.length;
      return ((i % n) + n) % n;
    }

    url(key) {
      return this.base.replace('yt-seq.json', `yt-seq-${key}.${this.meta.ext || 'webp'}`);
    }

    key(set, i) {
      return `${set}-${this.variant}-${String(i).padStart(2, '0')}`;
    }

    reset() {
      this.variant = this.mobile.matches ? 'm' : 'd';
      if (!this.meta) return;
      const [w, h] = this.meta.sizes[this.variant];
      this.canvas.width = this.front.width = w;
      this.canvas.height = this.front.height = h;
      this.frontKey = null;
      this.scene.classList.remove('is-ready');
      this.buildSketches();
      this.order = loadOrder(this.meta.frames);
      this.pump();
      this.requestDraw();
    }

    start() {
      this.started = true;
      this.pump();
    }

    // Current map first (interleaved with the shared layers), then the sketches of its
    // neighbours and their assembled frame (a click on a sketch starts there). Full neighbour
    // sequences and the other maps only load once the visitor starts switching.
    wanted() {
      const ids = this.maps.map((m) => m.id);
      const near = [ids[this.wrap(this.map + 1)], ids[this.wrap(this.map - 1)]];
      const outer = this.mobile.matches ? [] : [ids[this.wrap(this.map + 2)], ids[this.wrap(this.map - 2)]];
      // The current map's own sketch too: its box places the model when the carousel moves it.
      const sketches = this.sketchEls ? [...new Set([...near, ids[this.map], ...outer])].map((id) => `sketch-${id}`) : [];
      const sets = [[...new Set(['common', ids[this.map]])]];
      if (this.trans) sets.unshift(['common', ids[this.trans.to]]);
      sets.push(sketches);
      const keys = [];
      const add = (group, frames) => {
        for (const i of frames) for (const set of group) keys.push(this.key(set, i));
      };
      for (const group of sets) add(group, this.order);
      add(near, [0]);
      // Full sequences only for maps up to two steps away (pump runs again on every switch).
      if (this.interacted) {
        add(near, this.order);
        if (this.sketchEls) add(ids.map((id) => `sketch-${id}`), this.order);
        add([...new Set([...near, ...[2, -2].map((d) => ids[this.wrap(this.map + d)])])], this.order);
      }
      // The pose on screen first. Otherwise a switch starts on frame 0 (the closed front view,
      // which is preloaded) and that frame flashes over the exploded model.
      const pose = this.poseIndex();
      const first = [this.key('common', pose), this.key(ids[this.map], pose)];
      if (this.trans) first.push(this.key(ids[this.trans.to], pose), this.key('common', pose));
      const next = Math.ceil(this.pos ?? pose);
      if (next !== pose && next < this.meta.frames) {
        first.push(this.key('common', next), this.key(ids[this.map], next));
      }
      return [...new Set([...first, ...keys])];
    }

    // Atlases are fetched as compressed blobs and only decoded (off the main thread) while they
    // are near the visible frame, see prime().
    pump() {
      if (!this.meta || !this.started) return;
      const keys = this.wanted();
      for (const key of keys) {
        if (this.active >= 6) return;
        if (this.images.has(key)) continue;
        const entry = {};
        this.images.set(key, entry);
        this.active++;
        fetch(this.url(key), { headers: { Accept: this.accept } })
          .then((response) => (response.ok ? response.blob() : Promise.reject(response.status)))
          .then(
            (blob) => {
              entry.blob = blob;
              this.active--;
              this.requestDraw();
              this.pump();
            },
            () => {
              this.active--;
              this.pump();
            }
          );
      }
    }

    // Drawable = decoded.
    ready(set, i) {
      return !!this.images.get(this.key(set, i))?.bitmap;
    }

    src(key) {
      return this.images.get(key)?.bitmap;
    }

    decode(blob) {
      if (window.createImageBitmap) return createImageBitmap(blob);
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = reject;
        img.src = url;
      });
    }

    // Browsers drop decoded <img> pixels under memory pressure and decode them again inside
    // drawImage, on the main thread, which stalls scrolling. Instead the frames around the
    // visible one (further ahead in the scroll direction) and a few anchor frames are decoded
    // in the background and kept; everything else is released so phones keep their memory.
    prime() {
      if (!this.meta) return;
      const last = this.meta.frames - 1;
      const pos = this.pos ?? this.index;
      const target = this.target ?? pos;
      const ahead = this.mobile.matches ? 4 : 5;
      const dir = Math.sign(target - pos);
      let lo = Math.max(0, Math.floor(Math.min(pos, target)) - (dir < 0 ? ahead : 1));
      let hi = Math.min(last, Math.ceil(Math.max(pos, target)) + (dir > 0 ? ahead : 1));
      const most = ahead * 2;
      if (hi - lo > most) {
        if (dir < 0) lo = hi - most;
        else hi = lo + most;
      }
      const frames = new Set([0, Math.round(last / 3), Math.round((last * 2) / 3), last]);
      for (let f = lo; f <= hi; f++) frames.add(f);
      const ids = this.maps.map((m) => m.id);
      const sets = new Set(['common', ids[this.map]]);
      if (this.trans) sets.add(ids[this.trans.to]);
      const side = this.sideMap();
      if (side !== null) sets.add(ids[side]);
      this.sketchEls?.forEach((el, idx) => {
        if (Math.abs(this.rel(idx)) < this.reach() + 0.7) sets.add(el.set);
      });
      const keep = (this.keep = new Set());
      for (const f of frames) for (const set of sets) keep.add(this.key(set, f));
      // A click on a sketch starts from the assembled frame of that map.
      if (this.index <= 2) [-1, 1].forEach((d) => keep.add(this.key(ids[this.wrap(this.map + d)], 0)));
      for (const [key, entry] of this.images) {
        if (!entry.bitmap || keep.has(key)) continue;
        entry.bitmap.close?.();
        entry.bitmap = null;
      }
      for (const key of keep) {
        const entry = this.images.get(key);
        if (!entry?.blob || entry.bitmap || entry.decoding) continue;
        entry.decoding = true;
        this.decode(entry.blob).then(
          (bitmap) => {
            entry.decoding = false;
            if (!this.keep.has(key)) return bitmap.close?.();
            entry.bitmap = bitmap;
            this.requestDraw();
          },
          () => (entry.decoding = false)
        );
      }
    }

    // Scroll pose. Frame 0 is the closed front view; the last frame is fully exploded.
    // A missing atlas must never be replaced by a distant frame: frames 0–2 are still the
    // closed view, and dropping one in while the model is open flashes that frame on top.
    poseIndex() {
      if (!this.meta) return this.index || 0;
      const last = this.meta.frames - 1;
      const pos = this.pos === undefined ? this.index : this.pos;
      return Math.max(0, Math.min(last, Math.round(pos)));
    }

    // Only this pose and, while the scroll eases, the next rendered frame. Nothing further.
    poseCandidates() {
      const last = this.meta.frames - 1;
      const pos = this.pos === undefined ? this.index : this.pos;
      const out = [];
      for (const n of [Math.round(pos), Math.floor(pos), Math.ceil(pos)]) {
        const i = Math.max(0, Math.min(last, n));
        if (!out.includes(i)) out.push(i);
      }
      return out;
    }

    hasFrame(mapIndex, i) {
      if (!this.meta || mapIndex < 0 || mapIndex >= this.maps.length) return false;
      if (i < 0 || i >= this.meta.frames) return false;
      return this.ready('common', i) && this.ready(this.maps[mapIndex].id, i);
    }

    requestDraw() {
      if (this.drawQueued) return;
      this.drawQueued = true;
      requestAnimationFrame(() => {
        this.drawQueued = false;
        this.draw();
      });
    }

    rect(set, i, slot) {
      return this.meta.rects[this.variant][set][i][slot];
    }

    layer(set, i, slot) {
      const key = this.key(set, i);
      const r = this.rect(set, i, slot);
      const img = this.src(key);
      if (img && r[2]) this.ctx.drawImage(img, r[0], r[1], r[2], r[3], r[4], r[5], r[2], r[3]);
    }

    // Frame + spacer on the top canvas, cross-faded like the rest; only redrawn when it changes.
    drawFront(i0, i1 = i0, f = 0) {
      const pose = this.poseIndex();
      // Never paint the closed frame over an open one. Leave the previous bezel until the
      // matching pose is ready, so the two canvases cannot disagree for a frame.
      if (Math.abs(i0 - pose) > 1 || Math.abs((i1 ?? i0) - pose) > 1) return;
      const key = `${this.variant}|${i0}|${i1}|${f.toFixed(3)}`;
      if (key === this.frontKey) return;
      this.frontKey = key;
      const main = this.ctx;
      const ctx = (this.ctx = this.fctx);
      ctx.clearRect(0, 0, this.front.width, this.front.height);
      if (i0 === i1 || f < 0.02 || f > 0.98) {
        this.layer('common', f > 0.98 ? i1 : i0, 2);
      } else {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 1 - f;
        this.layer('common', i0, 2);
        ctx.globalAlpha = f;
        this.layer('common', i1, 2);
        ctx.restore();
      }
      this.ctx = main;
    }

    // Print or relief of one map moving like a sheet of paper: offset -1..1 of the print width,
    // lifted with a soft shadow. Assembled, it is clipped to the print (it changes inside the frame);
    // the clip opens up as the frame explodes.
    sheet(mapIndex, i, offset, slot, alpha) {
      const set = this.maps[mapIndex].id;
      if (!this.ready(set, i)) return;
      const img = this.src(this.key(set, i));
      const r = this.rect(set, i, slot);
      const p = this.rect(set, i, 0);
      if (!r[2] || alpha <= 0) return;
      const wave = Math.sin(Math.PI * Math.min(1, Math.abs(offset)));
      const open = (i / (this.meta.frames - 1)) * this.canvas.width;
      const ctx = this.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.rect(p[4] - open, p[5] - open, p[2] + 2 * open, p[3] + 2 * open);
      ctx.clip();
      ctx.globalAlpha = Math.min(1, alpha);
      if (wave > 0.01) {
        ctx.shadowColor = `rgba(0, 0, 0, ${0.4 * wave})`;
        const px = this.canvas.width / 1400;
        ctx.shadowBlur = 36 * wave * px;
        ctx.shadowOffsetY = 14 * wave * px;
      }
      ctx.translate(r[4] + r[2] / 2 + offset * p[2] * 0.45, r[5] + r[3] / 2 - wave * p[3] * 0.02);
      ctx.rotate(offset * 0.06);
      ctx.drawImage(img, r[0], r[1], r[2], r[3], -r[2] / 2, -r[3] / 2, r[2], r[3]);
      ctx.restore();
    }

    offscreen(n) {
      this.buffers ||= [];
      let buffer = this.buffers[n];
      if (!buffer) {
        const canvas = document.createElement('canvas');
        buffer = this.buffers[n] = { canvas, ctx: canvas.getContext('2d') };
      }
      if (buffer.canvas.width !== this.canvas.width || buffer.canvas.height !== this.canvas.height) {
        buffer.canvas.width = this.canvas.width;
        buffer.canvas.height = this.canvas.height;
        buffer.key = null;
      }
      return buffer;
    }

    // Flattened back, print, glass and relief of frame i, kept in one of two buffers so easing
    // between two frames only costs two drawImage calls per screen frame.
    stack(i, keep) {
      const key = `${this.variant}|${this.map}|${i}`;
      const pool = [this.offscreen(0), this.offscreen(1)];
      let buffer = pool.find((b) => b.key === key);
      if (buffer) return buffer;
      buffer = pool.find((b) => b.key !== keep);
      buffer.key = key;
      const main = this.ctx;
      this.ctx = buffer.ctx;
      buffer.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      const id = this.maps[this.map].id;
      for (const slot of [0, 1]) {
        this.layer('common', i, slot);
        this.layer(id, i, slot);
      }
      this.ctx = main;
      return buffer;
    }

    // Between two rendered frames the canvas cross-fades them ('lighter' adds the premultiplied
    // pixels, so (1 - f) * A + f * B is an exact dissolve, including transparency).
    drawBlend() {
      const last = this.meta.frames - 1;
      const i0 = Math.min(last, Math.floor(this.pos));
      const i1 = Math.min(last, i0 + 1);
      const f = this.pos - i0;
      const id = this.maps[this.map].id;
      const both = (i) => this.ready('common', i) && this.ready(id, i);
      if (f < 0.02 || f > 0.98 || i0 === i1 || !both(i0) || !both(i1)) return false;
      const a = this.stack(i0);
      const b = this.stack(i1, a.key);
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 1 - f;
      ctx.drawImage(a.canvas, 0, 0);
      ctx.globalAlpha = f;
      ctx.drawImage(b.canvas, 0, 0);
      ctx.restore();
      this.drawFront(i0, i1, f);
      this.scene.classList.add('is-ready');
      this.placeTags(f < 0.5 ? i0 : i1, this.map);
      return true;
    }

    draw() {
      if (!this.meta) return;
      this.placeSketches(this.poseIndex());
      this.prime();
      const t = this.trans;
      const sliding = this.sliding();
      if (!t && !sliding && this.drawBlend()) return;
      // One pose for the frame, the glass, the back and both sheets. The map that is not
      // decoded at that pose is skipped; a closed frame of the incoming map must not stand in.
      const owners = t ? [t.from, t.to] : [this.map];
      let i = -1;
      for (const frame of this.poseCandidates()) {
        if (owners.some((map) => this.hasFrame(map, frame))) {
          i = frame;
          break;
        }
      }
      if (i < 0) return;
      if (sliding) return this.drawCarousel(i);
      const toReady = !!t && this.hasFrame(t.to, i);
      const fromReady = !!t && this.hasFrame(t.from, i);
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.drawFront(i);
      // Exploded: the new sheet slides in on top of the old one, which slides back and fades.
      // Until the new sheet exists at this same pose, the old one stays put (no fade to empty).
      for (const [common, slot] of [[0, 0], [1, 1]]) {
        this.layer('common', i, common);
        if (!t || !toReady) {
          const map = t ? (fromReady ? t.from : this.map) : this.map;
          if (!t || fromReady || this.hasFrame(map, i)) this.layer(this.maps[map].id, i, slot);
          continue;
        }
        if (fromReady) this.sheet(t.from, i, -t.dir * t.t * 0.5, slot, (1 - t.t) * 1.6);
        this.sheet(t.to, i, t.dir * (1 - t.t), slot, t.t * 2.2);
      }
      this.scene.classList.add('is-ready');
      this.placeTags(i, t && toReady && t.t > 0.5 ? t.to : t ? t.from : this.map);
    }

    // Assembled carousel between two maps, in step with placeSketches: the frame stays put, the
    // map in it fades into its sketch (which then slides out behind the frame) down to blank
    // paper, and the next map materialises under its sketch once that has landed. The three
    // pictures are dissolved exactly ('lighter' with weights adding up to 1).
    drawCarousel(i) {
      const a = Math.abs(this.mpos - Math.round(this.mpos));
      const side = this.sideMap();
      const j = side === null ? -1 : this.sideFrame(side, i);
      const parts = [[this.full(this.map, i, 2), mapAlpha(a)]];
      if (j >= 0) parts.push([this.full(side, j, 3), mapAlpha(1 - a)]);
      const blank = 1 - parts.reduce((sum, [, w]) => sum + w, 0);
      if (blank > 0.001) parts.push([this.blank(i, 4), blank]);
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (const [buffer, w] of parts) {
        if (w <= 0.001) continue;
        ctx.globalAlpha = Math.min(1, w);
        ctx.drawImage(buffer.canvas, 0, 0);
      }
      ctx.restore();
      this.drawFront(i);
      this.scene.classList.add('is-ready');
      this.placeTags(i, this.map);
    }

    // Back, print, glass and relief of one map at frame i, flattened and kept while it is used.
    full(mapIndex, i, n) {
      const id = this.maps[mapIndex].id;
      return this.flat(n, `${this.variant}|${id}|${i}`, () => {
        for (const slot of [0, 1]) {
          this.layer('common', i, slot);
          this.layer(id, i, slot);
        }
      });
    }

    // Back, blank paper and glass: the frame between two maps.
    blank(i, n) {
      return this.flat(n, `${this.variant}|blank|${this.maps[this.map].id}|${i}`, () => {
        this.layer('common', i, 0);
        this.paper(this.maps[this.map].id, i);
        this.layer('common', i, 1);
      });
    }

    flat(n, key, paint) {
      const buffer = this.offscreen(n);
      if (buffer.key === key) return buffer;
      buffer.key = key;
      const main = this.ctx;
      this.ctx = buffer.ctx;
      buffer.ctx.clearRect(0, 0, buffer.canvas.width, buffer.canvas.height);
      paint();
      this.ctx = main;
      return buffer;
    }

    // Blank sheet in the shape of the print, in the paper tones of the rendered print (lit corner
    // to far corner), so a map switch goes through clean white paper instead of the back board.
    paper(set, i) {
      const key = this.key(set, i);
      const img = this.src(key);
      const r = this.rect(set, i, 0);
      if (!img || !r[2]) return;
      const tones = this.paperTones(key, img, r);
      const buffer = this.offscreen(5);
      const bctx = buffer.ctx;
      bctx.globalCompositeOperation = 'source-over';
      bctx.clearRect(0, 0, buffer.canvas.width, buffer.canvas.height);
      bctx.drawImage(img, r[0], r[1], r[2], r[3], r[4], r[5], r[2], r[3]);
      const gradient = bctx.createLinearGradient(r[4], r[5], r[4] + r[2], r[5] + r[3]);
      gradient.addColorStop(0, tones[0]);
      gradient.addColorStop(1, tones[1]);
      bctx.globalCompositeOperation = 'source-in';
      bctx.fillStyle = gradient;
      bctx.fillRect(r[4], r[5], r[2], r[3]);
      bctx.globalCompositeOperation = 'source-over';
      this.ctx.drawImage(buffer.canvas, 0, 0);
    }

    paperTones(key, img, r) {
      this.tones ||= new Map();
      if (this.tones.has(key)) return this.tones.get(key);
      let tones = ['#e4e4e2', '#cfcfcc'];
      try {
        const probe = document.createElement('canvas');
        probe.width = 8;
        probe.height = 8;
        const pctx = probe.getContext('2d', { willReadFrequently: true });
        const sample = (fx, fy) => {
          const x = r[0] + Math.round(r[2] * fx);
          const y = r[1] + Math.round(r[3] * fy);
          pctx.clearRect(0, 0, 8, 8);
          pctx.drawImage(img, x - 4, y - 4, 8, 8, 0, 0, 8, 8);
          const d = pctx.getImageData(0, 0, 8, 8).data;
          let best = [0, 0, 0];
          for (let k = 0; k < d.length; k += 4) {
            if (d[k + 3] > 250 && d[k] + d[k + 1] + d[k + 2] > best[0] + best[1] + best[2]) best = [d[k], d[k + 1], d[k + 2]];
          }
          return `rgb(${best.join(',')})`;
        };
        tones = [sample(0.04, 0.04), sample(0.96, 0.97)];
      } catch (error) {}
      this.tones.set(key, tones);
      return tones;
    }

    // ---------- sketches of the neighbouring maps ----------
    // Every map has a line drawing of its relief in the same 3D pose for every frame, so the
    // neighbours turn and pull apart with the scroll like the frame in the middle. They sit
    // between the map canvas and the frame canvas: always behind the frame, never in front.
    buildSketches() {
      this.sketchEls?.forEach((el) => el.remove());
      this.sketchEls = null;
      if (!this.meta.layers?.sketch || this.maps.length < 2) return;
      const [W, H] = [this.canvas.width, this.canvas.height];
      this.sketchEls = this.maps.map((m) => {
        const el = document.createElement('canvas');
        el.className = 'yt-seq__sketch';
        el.setAttribute('aria-hidden', 'true');
        el.set = `sketch-${m.id}`;
        el.frame = -1;
        // Only as big as the sketch gets over the whole sequence, not the full scene.
        const boxes = this.meta.rects[this.variant][el.set].map((f) => f[0]).filter((r) => r[2]);
        el.ox = Math.min(...boxes.map((r) => r[4]));
        el.oy = Math.min(...boxes.map((r) => r[5]));
        el.width = Math.max(...boxes.map((r) => r[4] + r[2])) - el.ox;
        el.height = Math.max(...boxes.map((r) => r[5] + r[3])) - el.oy;
        Object.assign(el.style, {
          left: `${(el.ox / W) * 100}%`,
          top: `${(el.oy / H) * 100}%`,
          width: `${(el.width / W) * 100}%`,
          height: `${(el.height / H) * 100}%`,
        });
        el.ctx = el.getContext('2d');
        el.hidden = true;
        this.front.before(el);
        return el;
      });
      if (!this.sketchClicks) {
        this.sketchClicks = true;
        // Sketches never take pointer events (they would block dragging the frame); a click
        // beside the frame switches to the map on that side instead.
        this.scene.addEventListener('click', (event) => {
          if (!this.sketchEls || this.trans || this.dragged) return;
          const box = this.scene.getBoundingClientRect();
          const frame = this.frameRect();
          const half = (frame[2] / 2 / this.canvas.width) * box.width;
          const dx = event.clientX - (box.left + ((frame[0] + frame[2] / 2) / this.canvas.width) * box.width);
          if (Math.abs(dx) > half) this.step(Math.sign(dx));
        });
      }
      this.measureSketches();
      this.placeSketches();
    }

    measureSketches() {
      if (!this.sketchEls) return;
      this.sideScale = this.mobile.matches ? 0.84 : 0.96;
    }

    // Sketch `el` at its rendered frame closest to `i`.
    drawSketch(el, i) {
      let frame = -1;
      for (let d = 0; d < this.meta.frames && frame < 0; d++) {
        for (const j of [i - d, i + d]) {
          if (j >= 0 && j < this.meta.frames && this.ready(el.set, j)) {
            frame = j;
            break;
          }
        }
      }
      if (frame < 0 || el.frame === frame) return el.frame >= 0;
      const r = this.rect(el.set, frame, 0);
      el.ctx.clearRect(0, 0, el.width, el.height);
      if (r[2]) el.ctx.drawImage(this.src(this.key(el.set, frame)), r[0], r[1], r[2], r[3], r[4] - el.ox, r[5] - el.oy, r[2], r[3]);
      el.frame = frame;
      el.style.transformOrigin = `${(((r[4] - el.ox + r[2] / 2) / el.width) * 100).toFixed(2)}% ${(((r[5] - el.oy + r[3] / 2) / el.height) * 100).toFixed(2)}%`;
      el.box = r;
      return true;
    }

    // Side slots shared by every map in frame i, so all sketches land on the same spots whatever
    // their size or where their relief sits on the print: the slots are centred on the average
    // sketch centre and spaced for the widest sketch. Slot 1 sits just outside everything drawn
    // in frame i (the frame pulls apart while exploding), but never fully off screen.
    slots(i, view) {
      const scale = view.width / this.canvas.width;
      const boxes = this.maps.map((m) => this.rect(`sketch-${m.id}`, i, 0)).filter((b) => b[2]);
      const cx = boxes.reduce((sum, b) => sum + b[4] + b[2] / 2, 0) / boxes.length;
      const cy = boxes.reduce((sum, b) => sum + b[5] + b[3] / 2, 0) / boxes.length;
      const widest = Math.max(...boxes.map((b) => b[2])) * scale;
      const id = this.maps[this.map].id;
      const drawn = [0, 1, 2].map((s) => this.rect('common', i, s)).concat([this.rect(id, i, 0)]).filter((b) => b[2]);
      const sw = widest * this.sideScale;
      const screenX = view.left + cx * scale * view.shown;
      const near = [-1, 1].map((side) => {
        const reach = Math.max(...drawn.map((b) => (side < 0 ? cx - b[4] : b[4] + b[2] - cx)));
        const room = side < 0 ? screenX : window.innerWidth - screenX;
        // At most this far, so at least 45 % of the sketch stays on screen.
        return Math.min(reach * scale + sw / 2 + (this.mobile.matches ? 6 : 20), room / view.shown + sw * 0.05);
      });
      const gap = (widest * (2 * this.sideScale - 0.18)) / 2 + 16;
      return { cx, cy, scale, near, gap };
    }

    // Frame outline [x, y, w, h] in canvas pixels, assembled.
    frameRect() {
      const r = this.meta.rects[this.variant].common[0][2];
      return [r[4], r[5], r[2], r[3]];
    }

    // Fractional map position: the carousel position, or the exploded sheet switch in progress.
    position() {
      const t = this.trans;
      return t ? t.from + (t.span ?? t.dir) * t.t : this.mpos;
    }

    rel(idx) {
      const n = this.maps.length;
      let d = (idx - this.position()) % n;
      if (d > n / 2) d -= n;
      if (d <= -n / 2) d += n;
      return d;
    }

    // How many sketches show fully on each side: one on phones, two on wider screens. A map
    // further away fades out beyond that (by reach + 0.7), before it wraps round to the other side.
    reach() {
      return this.mobile.matches ? 1 : Math.min(2, this.maps.length / 2 - 0.7);
    }

    // Carousel slot of a map at distance `rel` from the middle, in CSS px: next to the frame at 1,
    // one sketch further out (smaller, fainter) at 2.
    slot(el, rel, i, view) {
      const a = Math.abs(rel);
      const side = Math.sign(rel) || 1;
      const g = this.slots(i, view);
      const near = g.near[side < 0 ? 0 : 1];
      const k = travel(Math.min(a, 1), Math.min(3, (g.gap * (1 - SETTLE)) / near));
      const x = side * (a <= 1 ? k * near : near + (a - 1) * g.gap);
      // On the relief in the middle, on the shared slot centre at the side.
      const b = el.box;
      const dx = (g.cx - b[4] - b[2] / 2) * g.scale * k;
      const dy = (g.cy - b[5] - b[3] / 2) * g.scale * k;
      const out = clamp01(a - 1);
      const scale = a <= 1 ? 1 - (1 - this.sideScale) * k : this.sideScale - 0.18 * out;
      const turn = -side * (16 * k + 8 * out);
      return `perspective(1600px) translate(${(x + dx).toFixed(1)}px, ${dy.toFixed(1)}px) rotateY(${turn.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
    }

    view() {
      const box = this.scene.getBoundingClientRect();
      return { width: this.scene.offsetWidth, left: box.left, shown: box.width / this.scene.offsetWidth || 1 };
    }

    placeSketches(i = this.poseIndex()) {
      if (!this.sketchEls || !this.meta.rects[this.variant].common[i]) return;
      const reach = this.reach();
      const side = this.sideMap();
      const sideMissing = side !== null && this.sideFrame(side, i) < 0;
      let view = null;
      this.sketchEls.forEach((el, idx) => {
        const rel = this.rel(idx);
        const a = Math.abs(rel);
        // In the middle the rendered map shows instead (until it has loaded the sketch stays on
        // the blank paper); the outer sketches are quieter.
        const presence = a > 1 ? 1 - 0.4 * smooth(1, 2, a) : idx === side && sideMissing ? 1 : sketchAlpha(a);
        const opacity = presence * (1 - smooth(reach + 0.1, reach + 0.7, a));
        if (opacity <= 0 || !this.drawSketch(el, i)) {
          if (!el.hidden) {
            el.hidden = true;
            el.style.setProperty('--o', '0');
          }
          return;
        }
        el.hidden = false;
        view ||= this.view();
        el.style.transform = this.slot(el, rel, i, view);
        el.style.setProperty('--o', opacity.toFixed(3));
        el.style.setProperty('--ink', (1 - smooth(0.4, 0.8, a)).toFixed(3));
      });
    }

    // While the carousel is between two maps: the one beside the current map, on the side it
    // is moving towards.
    sideMap() {
      return this.sliding() ? this.wrap(this.map + Math.sign(this.mpos - Math.round(this.mpos))) : null;
    }

    sliding() {
      return this.carousel() && this.mpos !== Math.round(this.mpos);
    }

    // Frame of map `mapIndex` matching the exploded frame i (-1 while that frame is still loading).
    // Frame 0 is the closed frame from the front. It is always decoded, so it must not stand in
    // for an exploded frame: that flashes the front bezel over the open model.
    sideFrame(mapIndex, i) {
      const id = this.maps[mapIndex].id;
      const frames = i > 1 ? [i] : [i, 0, 1];
      return frames.find((f) => f >= 0 && this.ready('common', f) && this.ready(id, f)) ?? -1;
    }

    placeTags(i, mapIndex) {
      const frame = this.meta.anchors[this.variant][i];
      if (!frame) return;
      for (const tag of this.tags) {
        const kind = tag.dataset.kind;
        const point = kind === 'relief' ? frame.relief[this.maps[mapIndex].id] : frame[kind];
        if (!point) continue;
        tag.style.left = `${(point[0] * 100).toFixed(2)}%`;
        tag.style.top = `${(point[1] * 100).toFixed(2)}%`;
      }
    }

    // Scroll sets a target; the visible frame eases towards it so fast wheel steps don't jump.
    update(explode) {
      if (!this.meta) return;
      // Whole frames as targets: the eased position dissolves between them while moving and
      // always comes to rest on a sharp rendered frame.
      this.target = Math.round(explode * (this.meta.frames - 1));
      if (this.pos === undefined || this.reduceMotion.matches) this.pos = this.target;
      if (!this.easing) {
        this.easing = true;
        this.last = performance.now();
        requestAnimationFrame((now) => this.ease(now));
      }
    }

    ease(now) {
      const dt = Math.min(64, now - this.last);
      this.last = now;
      const diff = this.target - this.pos;
      this.pos = Math.abs(diff) < 0.004 ? this.target : this.pos + diff * (1 - Math.pow(0.78, dt / 16.7));
      this.index = Math.round(this.pos);
      this.draw();
      if (this.pos !== this.target) {
        requestAnimationFrame((time) => this.ease(time));
      } else {
        this.easing = false;
      }
    }

    // ---------- map switching ----------
    buildNav() {
      if (!this.nav || this.maps.length < 2) return;
      this.nameEl = this.nav.querySelector('.yt-seq__name');
      const dots = this.nav.querySelector('.yt-seq__dots');
      this.dots = this.maps.map((m, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'yt-seq__dot';
        dot.setAttribute('aria-label', m.name);
        dot.addEventListener('click', () => this.go(i, i > this.map ? 1 : -1));
        dots.appendChild(dot);
        return dot;
      });
      this.nav.querySelectorAll('[data-step]').forEach((button) => {
        button.addEventListener('click', () => this.step(Number(button.dataset.step)));
      });
      this.nav.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      this.nav.hidden = false;
      this.scene.classList.add('is-switchable');
      this.updateNav();
    }

    updateNav() {
      if (!this.nameEl) return;
      const current = this.trans && this.trans.t > 0.5 ? this.trans.to : this.map;
      this.nameEl.textContent = this.maps[current].name;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
    }

    // Assembled (or finishing a slide) the maps pass by as a carousel; exploded, the prints
    // change like sheets of paper inside the pulled-apart frame.
    carousel() {
      return !this.trans && !!this.sketchEls && (this.poseIndex() <= 1 || this.mpos !== Math.round(this.mpos));
    }

    step(dir) {
      if (this.carousel()) return this.slideTo(Math.round(this.goal ?? this.mpos) + dir);
      this.go(this.wrap((this.trans ? this.trans.to : this.map) + dir), dir);
    }

    go(to, dir) {
      if (this.carousel()) {
        const from = Math.round(this.goal ?? this.mpos);
        return this.slideTo(from + dir * this.wrap((to - this.wrap(from)) * dir));
      }
      if (this.trans) this.finish();
      if (to === this.map) return;
      this.interacted = true;
      const span = dir * this.wrap((to - this.map) * dir);
      this.trans = { from: this.map, to, dir, span, t: 0 };
      this.pump();
      this.animate(1);
    }

    // Carousel position; the canvas switches to the map nearest the middle.
    setPos(p) {
      this.mpos = p;
      const map = this.wrap(Math.round(p));
      if (map !== this.map) {
        this.map = map;
        this.pump();
      }
      this.updateNav();
      this.draw();
    }

    slideTo(goal) {
      cancelAnimationFrame(this.raf);
      this.interacted = true;
      this.goal = goal;
      const start = this.mpos;
      const dist = Math.abs(goal - start);
      const duration = this.reduceMotion.matches || dist < 0.001 ? 0 : 520 + 380 * Math.sqrt(dist);
      const begin = performance.now();
      const tick = (now) => {
        const k = duration ? easeOut(clamp01((now - begin) / duration)) : 1;
        this.setPos(start + (goal - start) * k);
        if (k < 1) {
          this.raf = requestAnimationFrame(tick);
        } else {
          this.goal = null;
          // Keep the unwrapped position small; rel() only looks at it modulo the map count.
          this.mpos = this.map;
          this.draw();
        }
      };
      this.raf = requestAnimationFrame(tick);
    }

    finish() {
      cancelAnimationFrame(this.raf);
      if (this.trans.t > 0) this.map = this.trans.to;
      this.trans = null;
      this.mpos = this.map;
      this.updateNav();
      this.requestDraw();
    }

    // Sheet switch while exploded. The slide waits until the incoming map is decoded at the
    // current pose; starting earlier used to substitute the closed front view for one frame.
    animate(goal) {
      cancelAnimationFrame(this.raf);
      const trans = this.trans;
      const startT = trans.t;
      const duration = this.reduceMotion.matches ? 0 : 650 * Math.abs(goal - startT) + 1;
      const opened = performance.now();
      let begin = opened;
      const tick = (now) => {
        if (this.trans !== trans) return;
        const ready = goal === 0 || this.hasFrame(trans.to, this.poseIndex());
        if (!ready) {
          if (now - opened > 4000) {
            this.trans = null;
            this.mpos = this.map;
            this.updateNav();
            this.draw();
            return;
          }
          this.pump();
          this.draw();
          begin = now;
          this.raf = requestAnimationFrame(tick);
          return;
        }
        const k = duration ? easeOut(clamp01((now - begin) / duration)) : 1;
        trans.t = startT + (goal - startT) * k;
        this.updateNav();
        this.draw();
        if (k < 1) {
          this.raf = requestAnimationFrame(tick);
        } else {
          if (goal === 1) this.map = trans.to;
          this.trans = null;
          this.mpos = this.map;
          this.updateNav();
          this.draw();
          this.pump();
        }
      };
      this.raf = requestAnimationFrame(tick);
    }

    // CSS px the carousel moves per map (middle to the first sketch), on screen.
    stepPx() {
      const width = this.scene.getBoundingClientRect().width;
      if (!this.sketchEls) return width * 0.6;
      const view = this.view();
      return this.slots(this.index, view).near[1] * view.shown || width * 0.6;
    }

    // Horizontal mouse drag / touch swipe on the frame (vertical swipes keep scrolling the page).
    // Assembled, the carousel follows the finger and keeps going after a flick; exploded, a swipe
    // pulls the next print in like a sheet of paper.
    bindDrag() {
      if (this.maps.length < 2) return;
      let drag = null;
      const width = () => this.scene.getBoundingClientRect().width;
      this.scene.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.dragged = false;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, active: false, samples: [] };
      });
      this.scene.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
            drag = null;
            return;
          }
          if (Math.abs(dx) < 10) return;
          drag.active = true;
          this.dragged = true;
          this.interacted = true;
          if (this.trans) this.finish();
          cancelAnimationFrame(this.raf);
          this.goal = null;
          drag.carousel = this.carousel();
          drag.from = this.mpos;
          drag.step = this.stepPx();
          drag.x = event.clientX;
          this.scene.setPointerCapture(event.pointerId);
          this.scene.classList.add('is-dragging');
        }
        const moved = event.clientX - drag.x;
        drag.samples.push([event.timeStamp, event.clientX]);
        if (drag.samples.length > 6) drag.samples.shift();
        if (drag.carousel) {
          this.setPos(drag.from - moved / drag.step);
          return;
        }
        const dir = moved < 0 ? 1 : -1;
        if (!this.trans || this.trans.dir !== dir) {
          this.trans = { from: this.map, to: this.wrap(this.map + dir), dir, t: 0 };
          this.pump();
        }
        this.trans.t = Math.min(1, Math.abs(moved) / (width() * 0.7));
        drag.lastDx = moved;
        this.updateNav();
        this.requestDraw();
      });
      const end = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const { active, carousel, samples, step } = drag;
        const [t0, x0] = samples[0] || [0, 0];
        const [t1, x1] = samples[samples.length - 1] || [0, 0];
        const velocity = t1 > t0 ? (x1 - x0) / (t1 - t0) : 0;
        const lastDx = drag.lastDx || 0;
        drag = null;
        this.scene.classList.remove('is-dragging');
        if (!active) return;
        const cancel = event.type === 'pointercancel';
        if (carousel) {
          // A flick carries on (about 180 ms of the release speed), at most two maps further.
          const fling = cancel ? 0 : Math.max(-2, Math.min(2, (-velocity * 180) / step));
          return this.slideTo(Math.round(this.mpos + fling));
        }
        if (!this.trans) return;
        const commit = !cancel && (this.trans.t > 0.25 || Math.abs(velocity) > 0.5 || Math.abs(lastDx) > width() * 0.35);
        this.animate(commit ? 1 : 0);
      };
      this.scene.addEventListener('pointerup', end);
      this.scene.addEventListener('pointercancel', end);

      let wheel = 0;
      let wheelTimer;
      this.scene.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          wheel += event.deltaX;
          clearTimeout(wheelTimer);
          wheelTimer = setTimeout(() => (wheel = 0), 200);
          if (Math.abs(wheel) > 60 && (this.carousel() || !this.trans)) {
            this.step(wheel > 0 ? 1 : -1);
            wheel = 0;
          }
        },
        { passive: false }
      );
    }
  }

  class YtExplodedHero extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onScroll = this.onScroll.bind(this);
      this.ticking = false;

      const seqConfig = this.querySelector('script[data-yt-seq]');
      if (seqConfig) {
        try {
          this.sequence = new YtSequence(this, JSON.parse(seqConfig.textContent));
        } catch (error) {
          this.sequence = null;
        }
      }

      if (this.classList.contains('yt-xhero--static') || this.reduceMotion.matches) {
        this.render(1);
        this.sequence?.start();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && this.sequence && !this.sequence.started) this.sequence.start();
        if (visible && !this.listening) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.listening = true;
        } else if (!visible && this.listening) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          this.listening = false;
        }
        this.onScroll();
      });
      this.observer.observe(this);
      this.onScroll();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    progress() {
      const rect = this.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      return total > 0 ? clamp01(-rect.top / total) : 0;
    }

    // Once the headline fades, the frame should sit in the middle of the viewport, whatever the
    // headline height or window size.
    center() {
      const seq = this.querySelector('.yt-seq');
      if (!seq) return;
      const middle = seq.offsetTop + seq.offsetHeight / 2;
      // On phones the layer legend sits under the frame, so the frame goes a little higher.
      const phone = window.matchMedia('(max-width: 860px)').matches;
      const target = phone ? 0.38 : 0.48;
      const seqShift = Math.round(window.innerHeight * target - middle);
      this.style.setProperty('--seq-shift', `${seqShift}px`);

      // Reassembled at the end, the frame sits right above the collection cards.
      const heading = this.querySelector('.yt-xhero__end h2');
      const canvas = seq.querySelector('.yt-seq__canvas');
      if (!heading || !canvas || !this.sequence?.meta) return;
      const sticky = this.querySelector('.yt-xhero__sticky');
      const top = (el) => {
        let y = 0;
        for (let node = el; node && node !== sticky; node = node.offsetParent) y += node.offsetTop;
        return y;
      };
      const seqTop = top(seq);
      const centre = seqTop + seq.offsetHeight / 2;
      const [, fy, , fh] = this.sequence.frameRect();
      const frameBottom = top(canvas) + ((fy + fh) / this.sequence.canvas.height) * canvas.offsetHeight;
      // Scale at the end (--copy 0, --end 1), see .yt-seq in yt-base.css.
      const style = getComputedStyle(seq);
      const scale = 1 + (parseFloat(style.getPropertyValue('--seq-grow')) || 0) - (parseFloat(style.getPropertyValue('--seq-shrink')) || 0);
      const gap = phone ? 40 : 56;
      const shift = top(heading) - gap - centre - scale * (frameBottom - centre);
      this.style.setProperty('--end-shift', `${Math.round(shift - seqShift)}px`);
    }

    onScroll(event) {
      if (event?.type === 'resize' || !this.centered) {
        this.centered = true;
        this.center();
      }
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.render(this.progress());
        this.ticking = false;
      });
    }

    // Timeline: hold → rotate + explode → labelled hold → reassemble → game cards.
    render(p) {
      const explode = smooth(0.08, 0.4, p) * (1 - smooth(0.62, 0.84, p));
      const rotate = smooth(0.06, 0.36, p) * (1 - smooth(0.66, 0.86, p));
      const labels = smooth(0.34, 0.42, p) * (1 - smooth(0.58, 0.64, p));
      const end = smooth(0.8, 0.94, p);
      this.style.setProperty('--e', explode.toFixed(4));
      this.style.setProperty('--r', rotate.toFixed(4));
      this.style.setProperty('--lbl', labels.toFixed(4));
      this.style.setProperty('--copy', (1 - smooth(0.04, 0.16, p)).toFixed(4));
      this.style.setProperty('--end', end.toFixed(4));
      this.classList.toggle('is-end', end > 0.5);
      this.sequence?.update(explode);
    }
  }

  customElements.define('yt-exploded-hero', YtExplodedHero);
}
